import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { callOffAPI } from '../../../lib/api';
import type { CallOff, CallOffStats } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsCarrier } from '../../../hooks/useIsCarrier';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Plus, CheckCircle, SendHorizonal, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import CallOffWizard from './CallOffWizard';
import CallOffDetailModal from './CallOffDetailModal';
import CallOffStatCards from './CallOffStatCards';
import CallOffListToolbar from './CallOffListToolbar';
import { buildCallOffListParams } from './callOffListParams';
import { useCallOffColumns } from './useCallOffColumns';
import { orderingFieldFor } from './callOffColumnConfig';
import { useCallOffExport } from './useCallOffExport';
import CallOffSupplierFilter from './CallOffSupplierFilter';
import CallOffListTable from './CallOffListTable';
import CallOffBulkActionDialog from './CallOffBulkActionDialog';
import CallOffHaulierFilterChip from './CallOffHaulierFilterChip';
import { useCallOffListActions } from './useCallOffListActions';
import {
  drillInLabel,
  HAULIER_LANDING_FILTER,
  type CallOffBucket,
  type StatCardDef,
  type SupplierOption,
} from './callOffListConfig';

export default function CallOffManagementTab() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCustomer = user?.company_type === 'customer' || user?.platform === 'CUSTOMER';
  // CR-7732: reading this list as a haulier rather than as a party to the
  // orders on it — no bulk actions, no draft/pending, and its own columns.
  const isHaulier = useIsCarrier();
  // CR-7782: every role picks and orders its own columns; which columns it is
  // offered depends on the role (the supplier never gets the haulage terms).
  const {
    orderedColumns, visibleColumns, isVisible, toggle: toggleColumn, move: moveColumn,
    showAll: showAllColumns, reset: resetColumns, hiddenCount,
  } = useCallOffColumns(isHaulier ? 'haulier' : isCustomer ? 'customer' : 'supplier', user?.id);
  const [callOffs, setCallOffs] = useState<CallOff[]>([]);
  const [stats, setStats] = useState<CallOffStats | null>(null);
  const [loading, setLoading] = useState(false);
  // CR-9121: a haulier lands on the loads awaiting it (the Logistics
  // Confirmed card) instead of every load it has ever carried. A default, not
  // a scope — the chip under the cards clears it.
  const [statusFilter, setStatusFilter] = useState(() => (isHaulier ? HAULIER_LANDING_FILTER : ''));
  // CR-7127: the shipment-stage card. A separate piece of state because it
  // is a separate backend filter (asn_stage, the derived ASN stage) rather
  // than the call-off's own detail_status. Selecting one clears the other so
  // the card row always shows a single active drill-in.
  const [asnStageFilter, setAsnStageFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<CallOffBucket>('active');
  // CR-7732: the header sort, asked of the server so it orders the whole
  // result set rather than the page on screen. Newest first until chosen.
  const [ordering, setOrdering] = useState('-created_at');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [search, setSearch] = useState('');
  // CR-7717: the search now reaches into each call-off's lines (part number,
  // description), so debounce it rather than query on every keystroke.
  const debouncedSearch = useDebouncedValue(search.trim());
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [selectedCallOffId, setSelectedCallOffId] = useState<number | null>(() => {
    const urlId = searchParams.get('calloff_id');
    return urlId ? parseInt(urlId, 10) : null;
  });
  const [editCallOff, setEditCallOff] = useState<CallOff | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const pageSize = 20;

  // Load suppliers for filter dropdown (customer only)
  useEffect(() => {
    if (!isCustomer) return;
    const loadSuppliers = async () => {
      try {
        const res = await callOffAPI.getSuppliers();
        setSuppliers(res.data || []);
      } catch {
        // Suppliers are optional for filtering
      }
    };
    loadSuppliers();
  }, [isCustomer]);

  // The list's query without paging. The export re-asks the server with the
  // same one, so the file and the screen cannot disagree about which loads
  // there are, or in what order (CR-7732).
  const listParams = useMemo(() => buildCallOffListParams({
    isHaulier, activeFilter, statusFilter, asnStageFilter,
    search: debouncedSearch, supplierFilter, ordering,
  }), [isHaulier, activeFilter, statusFilter, asnStageFilter, debouncedSearch, supplierFilter, ordering]);

  // CR-7782: hiding the column the list is sorted by would leave it sorted
  // with no header arrow to say so — go back to newest first instead.
  useEffect(() => {
    const sortedBy = ordering.replace(/^-/, '');
    if (sortedBy === 'created_at') return;
    const partyIsSupplier = isCustomer || isHaulier;
    if (!visibleColumns.some((c) => orderingFieldFor(c.id, partyIsSupplier) === sortedBy)) {
      setOrdering('-created_at');
      setPage(1);
    }
  }, [visibleColumns, ordering, isCustomer, isHaulier]);

  // The list's party column names the other side: the supplier to a customer
  // or a haulier, the customer to a supplier.
  const exportParams = useMemo(() => ({
    ...listParams, party: isCustomer || isHaulier ? 'supplier' : 'customer',
  }), [listParams, isCustomer, isHaulier]);
  // The export writes the columns the viewer left showing (CR-7732).
  const { exporting, runExport } = useCallOffExport(exportParams, isVisible, totalCount);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { ...listParams, page, page_size: pageSize };

      const [listRes, statsRes] = await Promise.all([
        callOffAPI.getCallOffs(params),
        callOffAPI.getCallOffStats(),
      ]);

      setCallOffs(listRes.data.results || []);
      setTotalCount(listRes.data.count || 0);
      setStats(statsRes.data);
      setSelectedIds(new Set());
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load call-offs';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, listParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const {
    bulkAction, setBulkAction, actionLoading,
    executeBulkAction, handleSingleSend, handleSingleCancel,
  } = useCallOffListActions(selectedIds, fetchData);

  // Which call-offs can be bulk-selected?
  // Customer: draft call-offs; Supplier: pending call-offs
  const selectableStatus = isCustomer ? 'draft' : 'pending';
  const selectableCallOffs = callOffs.filter((co) => co.detail_status === selectableStatus);
  const allSelectableChecked = selectableCallOffs.length > 0 && selectableCallOffs.every((co) => selectedIds.has(co.id));

  const toggleSelectAll = () => {
    if (allSelectableChecked) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableCallOffs.map((co) => co.id)));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCardClick = ({ kind, key }: StatCardDef, isActive: boolean) => {
    // CR-7717: clicking the card you are already on keeps it. It used to
    // clear the drill-in and fall back to the whole bucket, so a second click
    // (or a double-click) on e.g. Draft swapped an empty list for every
    // active call-off. Delivered only looked right because its bucket is
    // itself "delivered". Leave a card via the Active / Completed /
    // Cancelled toggle or by picking another card.
    if (isActive) return;
    if (kind === 'stage') {
      // A load in transit is still an active call-off, so switch
      // the toggle across or the drill-in comes back empty.
      setActiveFilter('active');
      setStatusFilter('');
      setAsnStageFilter(key);
    } else {
      // Auto-switch the toggle so the drill-in actually returns rows.
      if (key === 'cancelled') setActiveFilter('cancelled');
      else if (key === 'delivered') setActiveFilter('completed');
      else setActiveFilter('active');
      setStatusFilter(key);
      setAsnStageFilter('');
    }
    setPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // CR-9121: a haulier has no Active / Completed / Cancelled toggle to leave a
  // card by, so the card it is drilled into is shown as a chip it can clear,
  // and an empty drill-in names the card rather than saying "no call-offs".
  const drilledInto = isHaulier ? drillInLabel(statusFilter, asnStageFilter) : '';
  const clearDrillIn = () => { setStatusFilter(''); setAsnStageFilter(''); setPage(1); };
  const emptyState = drilledInto ? (
    <div className="space-y-3">
      <p>
        {debouncedSearch
          ? `No ${drilledInto} loads match your search.`
          : `No ${drilledInto} loads.`}
      </p>
      <Button variant="outline" size="sm" onClick={clearDrillIn}>Show all loads</Button>
    </div>
  ) : undefined;

  return (
    <div className="space-y-4">
      {/* Active / Completed / Cancelled toggle. CR-7732: not for a haulier —
          it is handed a load once the supplier confirms it and keeps it to
          delivery, so every load it can see is already "active" to it and the
          other two buckets only ever empty the list. */}
      {!isHaulier && (
      <div className="flex items-center gap-2">
        <Button
          variant={activeFilter === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setActiveFilter('active'); setStatusFilter(''); setAsnStageFilter(''); setPage(1); }}
        >
          Active
        </Button>
        <Button
          variant={activeFilter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setActiveFilter('completed'); setStatusFilter(''); setAsnStageFilter(''); setPage(1); }}
        >
          Completed
        </Button>
        <Button
          variant={activeFilter === 'cancelled' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setActiveFilter('cancelled'); setStatusFilter(''); setAsnStageFilter(''); setPage(1); }}
        >
          Cancelled
        </Button>
      </div>
      )}

      {/* Stats cards */}
      {stats && (
        <CallOffStatCards
          stats={stats}
          statusFilter={statusFilter}
          asnStageFilter={asnStageFilter}
          onCardClick={handleCardClick}
          isHaulier={isHaulier}
        />
      )}

      {drilledInto && <CallOffHaulierFilterChip label={drilledInto} onClear={clearDrillIn} />}

      {/* Filters & Actions */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* CR-7717: the status dropdown that sat here is gone — the stat
            cards above already filter by status. */}
        <Input
          type="search"
          placeholder="Search by reference, supplier, part number or description..."
          aria-label="Search call-offs"
          className="w-[26rem]"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />

        {/* Supplier filter (customer only) - searchable dropdown */}
        {isCustomer && suppliers.length > 0 && (
          <CallOffSupplierFilter
            suppliers={suppliers}
            supplierFilter={supplierFilter}
            setSupplierFilter={setSupplierFilter}
            onPageReset={() => setPage(1)}
          />
        )}

        {/* Bulk action buttons - Customer */}
        {selectedIds.size > 0 && isCustomer && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
            <Button size="sm" variant="outline" className="gap-1 text-green-700 border-green-300 hover:bg-green-50" onClick={() => setBulkAction('send')}>
              <SendHorizonal className="h-3.5 w-3.5" /> Send
            </Button>
            <Button size="sm" variant="outline" className="gap-1 text-red-700 border-red-300 hover:bg-red-50" onClick={() => setBulkAction('cancel')}>
              <XCircle className="h-3.5 w-3.5" /> Cancel
            </Button>
          </div>
        )}

        {/* Bulk action buttons - Supplier */}
        {selectedIds.size > 0 && !isCustomer && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
            <Button size="sm" variant="outline" className="gap-1 text-green-700 border-green-300 hover:bg-green-50" onClick={() => setBulkAction('confirm')}>
              <CheckCircle className="h-3.5 w-3.5" /> Confirm
            </Button>
          </div>
        )}

        <div className="flex-1" />
        <CallOffListToolbar
          columns={orderedColumns}
          isVisible={isVisible}
          onToggle={toggleColumn}
          onMove={moveColumn}
          onShowAll={showAllColumns}
          onReset={resetColumns}
          hiddenCount={hiddenCount}
          onExport={runExport}
          exportDisabled={loading || callOffs.length === 0}
          exporting={exporting}
        />
        {isCustomer && (
          <Button className="gap-2" onClick={() => setShowNewDialog(true)}>
            <Plus className="h-4 w-4" />
            New Call Off
          </Button>
        )}
      </div>

      <CallOffListTable
        callOffs={callOffs}
        loading={loading}
        isCustomer={isCustomer}
        selectableStatus={selectableStatus}
        selectableCount={selectableCallOffs.length}
        allSelectableChecked={allSelectableChecked}
        selectedIds={selectedIds}
        onToggleSelectAll={toggleSelectAll}
        onToggleSelect={toggleSelect}
        onRowClick={setSelectedCallOffId}
        onSend={(id) => handleSingleSend(id)}
        onCancel={handleSingleCancel}
        columns={visibleColumns}
        isHaulier={isHaulier}
        ordering={ordering}
        onSort={(next) => { setOrdering(next); setPage(1); }}
        emptyState={emptyState}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({totalCount} total)
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Bulk action confirmation dialog */}
      <CallOffBulkActionDialog
        bulkAction={bulkAction}
        selectedCount={selectedIds.size}
        actionLoading={actionLoading}
        onConfirm={executeBulkAction}
        onDismiss={() => setBulkAction(null)}
      />

      <CallOffWizard
        open={showNewDialog || editCallOff !== null}
        onClose={() => { setShowNewDialog(false); setEditCallOff(null); }}
        onCreated={fetchData}
        editCallOff={editCallOff}
      />

      <CallOffDetailModal
        open={selectedCallOffId !== null}
        callOffId={selectedCallOffId}
        onClose={() => {
          setSelectedCallOffId(null);
          if (searchParams.has('calloff_id')) {
            searchParams.delete('calloff_id');
            setSearchParams(searchParams, { replace: true });
          }
        }}
        onUpdated={fetchData}
        onEditInWizard={(co) => {
          setSelectedCallOffId(null);
          setEditCallOff(co);
        }}
      />
    </div>
  );
}
